import Navbar from "../components/Navbar";
import { useEffect, useState } from "react";
import { db, auth } from "../firebase/firebaseConfig";
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc } from "firebase/firestore";

function Leaderboard() {
  const [rawUsers, setRawUsers] = useState([]);
  const [finishedMatchIds, setFinishedMatchIds] = useState(new Set());
  const [leaders, setLeaders] = useState([]);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const user = auth.currentUser;

  const calculateAccuracy = (correct, userPredictions) => {
    if (!userPredictions || userPredictions.length === 0) return 0;
    const evaluatedCount = userPredictions.filter(p => finishedMatchIds.has(p.matchId)).length;
    if (evaluatedCount === 0) return 0;
    return Math.round((correct / evaluatedCount) * 100);
  };

  useEffect(() => {
    // 🛡️ Resize listener
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);

    // 🛡️ 0. Fetch Finished Matches
    const unsubMatches = onSnapshot(collection(db, "matches"), (snap) => {
      const ids = new Set();
      snap.forEach(doc => {
        if (doc.data().status === 'finished') ids.add(doc.data().id);
      });
      setFinishedMatchIds(ids);
    });

    // 🛡️ 1. Fetch Top Leaders, excluding the Admin account
    const q = query(collection(db, "users"), orderBy("points", "desc"), limit(25));
    const unsub = onSnapshot(q, (snapshot) => {
      setRawUsers(snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
      setLoading(false);
    });

    // 🛡️ 2. Fetch Current User specific stats if not in top 20
    if (user) {
      const fetchUser = async () => {
        const uDoc = await getDoc(doc(db, "users", user.uid));
        if (uDoc.exists()) setCurrentUserData(uDoc.data());
      };
      fetchUser();
    }

    return () => {
      unsub();
      unsubMatches();
      window.removeEventListener('resize', handleResize);
    };
  }, [user]);

  useEffect(() => {
    const validContenders = rawUsers
      .filter(userData => userData.email !== "parthadmin@gmail.com")
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        const accA = calculateAccuracy(a.correctPredictions, a.predictions || []);
        const accB = calculateAccuracy(b.correctPredictions, b.predictions || []);
        if (accB !== accA) return accB - accA;
        return (b.correctPredictions || 0) - (a.correctPredictions || 0);
      });

    const users = validContenders.map((user, index) => ({
      ...user,
      rank: index + 1
    }));
    setLeaders(users);
  // react-hooks/exhaustive-deps intentionally ignored because calculateAccuracy depends on finishedMatchIds which is listed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawUsers, finishedMatchIds]);

  const topThree = leaders.slice(0, 3);
  const others = leaders.slice(3);

  // Reorder for Podium: [2nd, 1st, 3rd] - On mobile just [1st, 2nd, 3rd]
  const podiumOrder = isMobile
    ? [topThree[0] || null, topThree[1] || null, topThree[2] || null]
    : [topThree[1] || null, topThree[0] || null, topThree[2] || null];

  const PodiumItem = ({ player, rankPosition }) => {
    if (!player) return null;

    const isFirst = player.rank === 1;
    const color = player.rank === 1 ? '#FFD700' : player.rank === 2 ? '#C0C0C0' : '#CD7F32';

    return (
      <div style={{
        ...styles.podiumCard,
        height: isMobile ? 'auto' : (isFirst ? '320px' : '280px'),
        marginTop: isMobile ? '0' : (isFirst ? '0' : '40px'),
        width: isMobile ? '100%' : '280px'
      }}>
        <div style={{ ...styles.podiumAvatarWrap, borderColor: color, width: isFirst ? '100px' : '80px', height: isFirst ? '100px' : '80px' }}>
          {player.profileImage ? (
            <img src={player.profileImage} alt="P" style={styles.podiumImg} />
          ) : (
            <div style={styles.podiumChar}>{player.displayName?.charAt(0) || "P"}</div>
          )}
          <div style={{ ...styles.rankBadge, background: color }}>{player.rank}</div>
        </div>
        <div style={{ textAlign: isMobile ? 'left' : 'center', flex: isMobile ? 1 : 0 }}>
          <h3 style={styles.podiumName}>{player.displayName}</h3>
          <div style={styles.podiumPoints}>{player.points.toLocaleString()} <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>PTS</span></div>
          <div style={styles.podiumAcc}>{calculateAccuracy(player.correctPredictions, player.predictions || [])}% ACCURACY</div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: 'var(--stadium-gradient)', paddingBottom: '3rem' }}>
      <Navbar />
      <div className="app-container">
        <header style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 className="gradient-text" style={{ fontSize: isMobile ? '2.5rem' : '3.5rem' }}>HALL OF FAME</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Compete with the elite. Rule the IPL Arena.</p>
        </header>

        {loading ? (
          <div style={styles.loader}>Syncing Rankings...</div>
        ) : (
          <>
            {/* 🏆 Podium Section */}
            <div style={{ ...styles.podiumContainer, flexDirection: isMobile ? 'column' : 'row' }}>
              {podiumOrder.map((p, i) => (
                <PodiumItem key={i} player={p} />
              ))}
            </div>

            {/* 📋 Table Section */}
            <div className="glass-morphism animate-fade-in" style={styles.tableCard}>
              <div style={styles.tableHead}>
                <div style={{ ...styles.colRank, width: '60px' }}>#</div>
                <div style={styles.colUser}>CONTENDER</div>
                <div style={styles.colPoints}>SCORE</div>
                {!isMobile && <div style={styles.colCorrect}>HITs</div>}
                {!isMobile && <div style={styles.colAcc}>EFFICIENCY</div>}
              </div>

              <div style={styles.tableBody}>
                {others.map((p) => (
                  <div key={p.uid} style={{ ...styles.tableRow, background: user?.uid === p.uid ? 'rgba(0,118,255,0.08)' : 'transparent' }}>
                    <div style={{ ...styles.colRank, width: '60px' }}>{p.rank}</div>
                    <div style={styles.colUser}>
                      <div style={styles.tableAvatar}>
                        {p.profileImage ? <img src={p.profileImage} alt="" style={styles.tableImg} /> : <span style={{ fontSize: '0.9rem', fontWeight: '900' }}>{p.displayName?.charAt(0)}</span>}
                      </div>
                      <span>{p.displayName}</span>
                    </div>
                    <div style={{ ...styles.colPoints, fontWeight: '900', color: 'var(--primary)' }}>{p.points}</div>
                    {!isMobile && <div style={styles.colCorrect}>{p.correctPredictions || 0}</div>}
                    {!isMobile && (
                      <div style={styles.colAcc}>
                        <div style={styles.accBarWrap}>
                          <div style={{ ...styles.accBarFill, width: `${calculateAccuracy(p.correctPredictions, p.predictions || [])}%` }}></div>
                          <span style={styles.accVal}>{calculateAccuracy(p.correctPredictions, p.predictions || [])}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Current User Sticky */}
              {user && user.email !== "parthadmin@gmail.com" && !leaders.find(l => l.uid === user.uid) && currentUserData && (
                <div style={styles.currentUserRow}>
                  <div style={{ ...styles.colRank, width: '60px' }}>#</div>
                  <div style={styles.colUser}>
                    <div style={{ ...styles.tableAvatar, background: 'var(--primary)', color: 'white', fontWeight: '900', fontSize: '0.6rem' }}>YOU</div>
                    <span>{currentUserData.displayName}</span>
                  </div>
                  <div style={styles.colPoints}>{currentUserData.points}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  podiumContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '3rem' },
  podiumCard: { background: 'var(--bg-secondary)', borderRadius: '24px', border: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', transition: '0.3s', backdropFilter: 'blur(10px)', boxShadow: 'var(--card-shadow)' },
  podiumAvatarWrap: { borderRadius: '50%', border: '4px solid', position: 'relative', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.03)' },
  podiumImg: { width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' },
  podiumChar: { fontSize: '2rem', fontWeight: '900' },
  rankBadge: { position: 'absolute', bottom: '-2px', right: '-2px', width: '28px', height: '28px', borderRadius: '50%', color: '#000', fontWeight: '900', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', border: '2px solid #000' },
  podiumName: { fontSize: '1.1rem', fontWeight: '800', marginBottom: '0.1rem' },
  podiumPoints: { fontSize: '1.6rem', fontWeight: '900', color: 'var(--primary)', marginBottom: '0.4rem' },
  podiumAcc: { fontSize: '0.65rem', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '0.2rem 0.6rem', borderRadius: '50px', fontWeight: '800', letterSpacing: '0.05em' },
  tableCard: { width: '100%', borderRadius: '24px', overflow: 'hidden' },
  tableHead: { display: 'flex', padding: '1.2rem', color: 'var(--text-secondary)', fontSize: '0.7rem', fontWeight: '900', letterSpacing: '0.1em' },
  tableRow: { display: 'flex', padding: '1rem', borderBottom: '1px solid var(--glass-border)', alignItems: 'center', transition: '0.2s' },
  colRank: { textAlign: 'center', fontWeight: '900', opacity: 0.5 },
  colUser: { flex: 1, display: 'flex', alignItems: 'center', gap: '0.8rem', fontWeight: '700', fontSize: '0.9rem' },
  colPoints: { width: '90px', textAlign: 'center' },
  colCorrect: { width: '80px', textAlign: 'center', opacity: 0.7 },
  colAcc: { width: '150px' },
  tableAvatar: { width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: "center", overflow: 'hidden' },
  tableImg: { width: '100%', height: '100%', objectFit: 'cover' },
  accBarWrap: { width: '100%', height: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '50px', position: 'relative', overflow: 'hidden' },
  accBarFill: { height: '100%', background: 'var(--primary)', position: 'absolute', left: 0, top: 0, borderRadius: '50px' },
  accVal: { position: 'absolute', right: '10px', top: '-1px', fontSize: '0.55rem', fontWeight: '900' },
  currentUserRow: { display: 'flex', padding: '1rem', background: 'rgba(0,118,255,0.15)', borderRadius: '20px', margin: '0.8rem', border: '1.5px solid var(--primary)', alignItems: 'center' },
  loader: { textAlign: 'center', padding: '10rem', color: 'var(--primary)', fontSize: '1.2rem', fontWeight: '800' }
};

export default Leaderboard;